<template>
  <div>
    <PublicPageTemplate>
      <template v-slot:content>
        <TWLoadingSpinner v-if="loading" />
        <PublicPageHeader :title="$t('login.pageTitle')" />
        <form @submit.prevent="attemptLogin">
          <FloatingLabelInput
              type="text"
              @input="clearEmailErrorMessage"
              v-model.trim="email"
              :has-error="emailErrorMessage"
              :placeholder="$t('login.emailAddress')"
              v-validate="'required|isTrimmedEmail'"
              data-vv-name="email"
              data-vv-as="email"
              data-vv-scope="login"
              data-test="login-email"
              data-hj-suppress
              data-ec="login"
              data-et="email_field"
              data-testid="login-page-email-input"
          />
          <PublicPageValidationError :errorMessage="emailErrorMessage" />
          <div>
            <FloatingLabelInput
                type="password"
                @input="clearPasswordErrorMessage"
                v-model="password"
                :has-error="passwordErrorMessage"
                :placeholder="$t('login.password')"
                v-validate="'required'"
                data-vv-name="password"
                data-vv-as="password"
                data-vv-scope="login"
                data-test="login-password"
                data-hj-suppress
                data-ec="login"
                data-et="password_field"
                data-testid="login-page-password-input"
            />
            <PublicPageValidationError :errorMessage="passwordErrorMessage" />
          </div>
          <RouterLink
              :to="{ name: 'ForgotPassword' }"
              class="float-right mb-sm text-darkFont hover:text-primary active:text-darkFont font-normal"
              data-test="login-forgot-password"
              data-ec="login"
              data-et="forgot_password_link"
              data-testid="login-page-forgotpassword-link">
            {{ $t('login.forgotPassword') }}
          </RouterLink>
          <PublicPageSubmitButton
              :text="$t('login.loginVerb')"
              data-test="login-button"
              data-ec="login"
              data-et="login_button"
              data-testid="login-page-login-button"
          />
        </form>
        <div class="text-center">
          <i18n path="login.newToSignable"
                tag="p"
                data-testid="login-page-signup-text"
          >
            <template v-slot:link>
              <RouterLink
                  :to="{ name: 'Register' }"
                  class="mt-base text-darkFont hover:text-primary active:text-darkFont font-normal"
                  data-ec="login"
                  data-et="register_link"
                  data-testid="login-page-signup-link">
                {{ $t('login.signUp') }}
              </RouterLink>
            </template>
          </i18n>
        </div>
      </template>
    </PublicPageTemplate>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TWLoadingSpinner from '@/components/tailwind/LoadingSpinner.vue';
import PublicPageTemplate from '@/components/tailwind/publicPages/PublicPageTemplate.vue';
import PublicPageHeader from '@/components/tailwind/publicPages/PublicPageHeader.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import PublicPageSubmitButton from '@/components/tailwind/publicPages/PublicPageSubmitButton.vue';
import PublicPageValidationError from '@/components/tailwind/publicPages/PublicPageValidationError.vue';

export default {
  name: 'LoginPage',
  inject: {
    $validator: '$validator',
  },
  components: {
    FloatingLabelInput,
    TWLoadingSpinner,
    PublicPageTemplate,
    PublicPageHeader,
    PublicPageSubmitButton,
    PublicPageValidationError,
  },
  data() {
    return {
      email: null,
      password: null,
      loading: false,
      emailErrorMessage: null,
      passwordErrorMessage: null,
    };
  },
  computed: {
    ...mapGetters({
      featureEnabled: 'featureFlags/featureEnabled',
    }),
  },
  methods: {
    ...mapActions('user', [
      'LOGIN',
      'GET_USER_FROM_TOKEN',
    ]),
    clearPasswordErrorMessage() {
      this.passwordErrorMessage = null;
    },
    clearEmailErrorMessage() {
      this.emailErrorMessage = null;
    },
    async attemptLogin() {
      const validated = await this.$validator.validateAll('login');

      if (!validated) {
        const errors = this.errors.items;
        errors.forEach((error) => {
          if (error.field === 'password') {
            this.passwordErrorMessage = error.msg;
          }
          if (error.field === 'email') {
            this.emailErrorMessage = error.msg;
          }
        });
        return;
      }

      this.loading = true;

      await this.$store.dispatch('auth/LOGIN', {
        email: this.email,
        password: this.password,
      }).then((response) => {
        // Explicitly detaching vee-validator for login form items
        this.$validator.detach('email', 'login');
        this.$validator.detach('password', 'login');

        if (response.mfa_setup_required) {
          this.$router.push({ name: 'SetupMFA' });
          return;
        }
        if (response.mfa_authentication_required) {
          this.$router.push({ name: 'MFA' });
          return;
        }

        // If we have a previous route after a force logout redirect to there, otherwise go to the Envelopes page
        const { interceptedFrom } = this.$route.params;
        const shouldRedirectToPreviousRoute = interceptedFrom && !['Login', 'SetupMFA', 'MFA', 'MFARecoveryCode', 'useRecoveryCode'].includes(interceptedFrom.name);
        if (shouldRedirectToPreviousRoute) {
              this.$router.push(interceptedFrom);
            } else {
          this.$router.push({ name: 'Envelopes' });
        }
      }).catch(() => {
        // If an error occurs we rely on the error interceptors in src/utils/http.js
      });

      this.loading = false;
    },
  },
};
</script>
